<script setup lang="ts">
import { ref, watch } from 'vue'
import { aiService, type AIExecutiveBriefingResponse } from '@/api/aiService'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits(['close'])

const loading = ref(false)
const selectedLang = ref<'sw' | 'en'>('sw')
const briefingData = ref<AIExecutiveBriefingResponse | null>(null)
const errorMsg = ref('')

const fetchBriefing = async () => {
  errorMsg.value = ''
  loading.value = true
  try {
    const res = await aiService.getExecutiveBriefing(selectedLang.value)
    briefingData.value = res
  } catch (err) {
    console.error('Failed to generate executive briefing:', err)
    errorMsg.value = selectedLang.value === 'sw'
      ? 'Hitilafu imetokea wakati wa kuunda taarifa ya mtendaji. Jaribu tena.'
      : 'Failed to generate briefing. Please try again.'
  } finally {
    loading.value = false
  }
}

watch(() => props.isOpen, (newVal) => {
  if (newVal && !briefingData.value) {
    fetchBriefing()
  }
})

const toggleLang = (lang: 'sw' | 'en') => {
  selectedLang.value = lang
  fetchBriefing()
}

const closeModal = () => {
  emit('close')
}

const printBriefing = () => {
  window.print()
}

const formatBriefing = (text: string) => {
  if (!text) return ''
  let formatted = text

  if (formatted.includes('|')) {
    const lines = formatted.split('\n')
    let inTable = false
    let tableHtml = '<div class="overflow-x-auto my-4"><table class="w-full text-xs border-collapse border border-slate-700 bg-slate-800/90 rounded-2xl overflow-hidden shadow-lg">'
    const resultLines: string[] = []

    for (let line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        if (trimmed.includes('---')) continue

        const cells = trimmed.split('|').slice(1, -1).map(c => c.trim())
        if (!inTable) {
          inTable = true
          tableHtml += '<thead><tr class="bg-indigo-950 text-indigo-200 border-b border-slate-700">'
          cells.forEach(c => { tableHtml += `<th class="p-3 text-left font-bold border-r border-slate-700/50">${c}</th>` })
          tableHtml += '</tr></thead><tbody>'
        } else {
          tableHtml += '<tr class="border-b border-slate-700/50 hover:bg-slate-700/40">'
          cells.forEach(c => { tableHtml += `<td class="p-3 border-r border-slate-700/50 text-slate-200">${c}</td>` })
          tableHtml += '</tr>'
        }
      } else {
        if (inTable) {
          inTable = false
          tableHtml += '</tbody></table></div>'
          resultLines.push(tableHtml)
          tableHtml = '<div class="overflow-x-auto my-4"><table class="w-full text-xs border-collapse border border-slate-700 bg-slate-800/90 rounded-2xl overflow-hidden shadow-lg">'
        }
        resultLines.push(line)
      }
    }
    if (inTable) {
      tableHtml += '</tbody></table></div>'
      resultLines.push(tableHtml)
    }
    formatted = resultLines.join('\n')
  }

  formatted = formatted
    .replace(/^### (.*$)/gim, '<h3 class="text-xs font-bold text-indigo-300 mt-4 mb-2">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-sm font-bold text-amber-400 mt-5 mb-3 border-b border-slate-700 pb-1.5">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-bold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="text-slate-300">$1</em>')
    .replace(/`([^`]+)`/g, '<code class="bg-slate-800 text-indigo-300 px-1 py-0.5 rounded text-xs font-mono">$1</code>')
    .replace(/\n/g, '<br/>')

  return formatted
}
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-fadeIn print:p-0 print:bg-white">
    <div class="relative w-full max-w-4xl bg-slate-900 border border-amber-500/30 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] font-sans text-xs print:max-h-none print:border-none print:shadow-none print:text-black">
      
      <!-- Header -->
      <div class="p-6 bg-gradient-to-r from-slate-900 via-amber-950 to-slate-900 border-b border-slate-800 flex items-center justify-between print:hidden">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-indigo-600 flex items-center justify-center text-white text-2xl shadow-xl shadow-amber-500/20">
            👔
          </div>
          <div>
            <h2 class="text-lg font-black text-white tracking-wide">
              {{ selectedLang === 'sw' ? 'Taarifa ya Mtendaji Mkuu (1-Click Executive AI Briefing)' : '1-Click C-Level Executive Briefing' }}
            </h2>
            <p class="text-xs text-amber-200/80">
              {{ selectedLang === 'sw' ? 'Muhtasari wa kiutendaji wa shamba na maduka kwa maamuzi ya kiongozi' : 'Comprehensive multi-module KPI synthesis & strategic C-Suite directives' }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <div class="flex items-center bg-slate-800 p-0.5 rounded-xl border border-slate-700">
            <button
              @click="toggleLang('sw')"
              class="px-3 py-1 text-xs font-bold rounded-lg transition-colors"
              :class="selectedLang === 'sw' ? 'bg-amber-600 text-white shadow' : 'text-slate-400 hover:text-white'"
            >
              Kiswahili
            </button>
            <button
              @click="toggleLang('en')"
              class="px-3 py-1 text-xs font-bold rounded-lg transition-colors"
              :class="selectedLang === 'en' ? 'bg-amber-600 text-white shadow' : 'text-slate-400 hover:text-white'"
            >
              English
            </button>
          </div>

          <button
            @click="fetchBriefing"
            :disabled="loading"
            class="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl border border-slate-700 transition-colors text-xs font-bold flex items-center gap-1.5"
          >
            <span>🔄</span>
            <span>Refresh</span>
          </button>

          <button
            @click="printBriefing"
            class="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl transition-colors text-xs font-bold flex items-center gap-1.5 shadow"
          >
            <span>🖨️</span>
            <span>Print</span>
          </button>

          <button @click="closeModal" class="w-8 h-8 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors">
            ✕
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="flex-1 p-8 overflow-y-auto custom-scrollbar">
        <div v-if="loading" class="py-20 flex flex-col items-center justify-center gap-4 text-amber-300">
          <div class="animate-spin h-10 w-10 border-4 border-amber-400 border-t-transparent rounded-full"></div>
          <p class="text-sm font-bold tracking-wide">Scanning database tables & compiling C-Level Executive Briefing...</p>
        </div>

        <div v-else-if="briefingData" class="space-y-6">
          <div class="text-slate-200 leading-relaxed text-xs space-y-3" v-html="formatBriefing(briefingData.briefing)"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #334155;
  border-radius: 9999px;
}
</style>
